import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { ShieldCheck, CheckCircle2, LogIn, Loader2, AlertCircle, UserCheck } from 'lucide-react';
import { toast } from 'sonner';

export default function StaffActivate() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';

  const [loading, setLoading] = useState(true);
  const [inviteInfo, setInviteInfo] = useState(null);
  const [error, setError] = useState('');

  // New user flow
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');

  // Existing user flow
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');

  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!token) { setError('No invitation token found.'); setLoading(false); return; }
    api.get(`/staff/invite-info?token=${token}`)
      .then(({ data }) => {
        if (data.status === 'pending') {
          setInviteInfo(data);
        } else {
          setError(data.message || 'This invitation is no longer valid.');
        }
      })
      .catch(e => setError(e.response?.data?.detail || 'Invalid invitation link.'))
      .finally(() => setLoading(false));
  }, [token]);

  // New user: activate with password
  const activateNew = async (e) => {
    e.preventDefault();
    if (password.length < 8) return toast.error('Password must be at least 8 characters.');
    if (password !== confirm) return toast.error('Passwords do not match.');
    setSaving(true);
    try {
      const { data } = await api.post('/staff/activate', { token, password });
      setResult(data);
      toast.success('Account activated!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Activation failed.');
    } finally { setSaving(false); }
  };

  // Existing user: sign in & accept
  const acceptExisting = async (e) => {
    e.preventDefault();
    if (!loginEmail || !loginPassword) return toast.error('Please enter your email and password.');
    setSaving(true);
    try {
      const { data } = await api.post('/staff/accept-invite', { token, email: loginEmail, password: loginPassword });
      setResult(data);
      toast.success(data.already_member ? 'You already have access!' : 'Invitation accepted!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Sign in failed. Check your credentials.');
    } finally { setSaving(false); }
  };

  // Already logged in: accept directly
  const acceptLoggedIn = async () => {
    const existingToken = localStorage.getItem('access_token');
    if (!existingToken) return; // shouldn't happen
    setSaving(true);
    try {
      // Try activating with empty password — backend detects existing user
      const { data } = await api.post('/staff/activate', { token, password: '' });
      setResult(data);
      toast.success(data.already_member ? 'You already have access!' : 'Invitation accepted!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to accept invitation.');
    } finally { setSaving(false); }
  };

  // Loading
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
        <Card className="w-full max-w-md"><CardContent className="flex flex-col items-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
          <p className="text-sm text-gray-500 mt-3">Checking invitation...</p>
        </CardContent></Card>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
        <Card className="w-full max-w-md"><CardHeader className="text-center">
          <AlertCircle className="h-12 w-12 mx-auto text-red-500" />
          <CardTitle className="mt-3 text-lg">Invitation Issue</CardTitle>
          <p className="text-sm text-gray-500 mt-2">{error}</p>
        </CardHeader><CardContent>
          <Button className="w-full" variant="outline" onClick={() => navigate('/')}>Go to APC EstateFlow</Button>
        </CardContent></Card>
      </div>
    );
  }

  // Success / result
  if (result) {
    const portalName = result.portal_display_name || inviteInfo?.portal_display_name || 'APC EstateFlow Portal';
    const loginPath = result.portal_login_path || inviteInfo?.portal_login_path || '/';
    const dashPath = result.portal_dashboard_path || inviteInfo?.portal_dashboard_path || loginPath;
    const isLoggedIn = !!localStorage.getItem('access_token');
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
        <Card className="w-full max-w-md" data-testid="staff-activation-success">
          <CardHeader className="text-center">
            <CheckCircle2 className="h-14 w-14 mx-auto text-emerald-500" />
            <CardTitle className="mt-4 text-xl">
              {result.already_member ? 'Already a Member' : result.existing_user ? 'Invitation Accepted' : 'Account Activated'}
            </CardTitle>
            <p className="text-sm text-gray-500 mt-2">
              {result.already_member
                ? `You already have access to ${result.organization_name}.`
                : result.existing_user
                  ? `${result.organization_name} has been added to your APC EstateFlow account.`
                  : 'Your APC EstateFlow account has been activated successfully.'}
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-4 text-center space-y-1">
              <p className="font-semibold text-base">{result.organization_name}</p>
              <p className="text-sm text-gray-500">{result.staff_role_name}</p>
            </div>
            <Button className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-3"
              onClick={() => navigate(isLoggedIn ? dashPath : loginPath)} data-testid="go-to-portal-btn">
              <LogIn className="mr-2 h-4 w-4" />
              {isLoggedIn ? `Go to ${portalName}` : `Sign In to ${portalName}`}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const orgName = inviteInfo?.organization_name || 'Organization';
  const roleName = inviteInfo?.staff_role_name || 'Team Member';

  // Already a member
  if (inviteInfo?.already_member) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
        <Card className="w-full max-w-md"><CardHeader className="text-center">
          <UserCheck className="h-12 w-12 mx-auto text-blue-500" />
          <CardTitle className="mt-3">Already a Member</CardTitle>
          <p className="text-sm text-gray-500 mt-2">You already have access to <strong>{orgName}</strong>.</p>
        </CardHeader><CardContent>
          <Button className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
            onClick={() => navigate(inviteInfo.portal_login_path || '/')}>
            <LogIn className="mr-2 h-4 w-4" /> Sign In to {inviteInfo.portal_display_name || 'Portal'}
          </Button>
        </CardContent></Card>
      </div>
    );
  }

  // Existing user: check if already logged in
  const isLoggedIn = !!localStorage.getItem('access_token');
  if (inviteInfo?.is_existing_user && isLoggedIn) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <UserCheck className="h-11 w-11 mx-auto text-emerald-600" />
            <CardTitle className="mt-3">Accept Invitation</CardTitle>
            <p className="text-sm text-gray-500 mt-1">
              You've been invited to join <strong>{orgName}</strong> as <strong>{roleName}</strong>.
            </p>
          </CardHeader>
          <CardContent>
            <Button className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-3"
              disabled={saving} onClick={acceptLoggedIn} data-testid="accept-invite-btn">
              {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Accepting...</> : <>Accept {orgName} Membership</>}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Existing user: not logged in — sign in & accept
  if (inviteInfo?.is_existing_user) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <ShieldCheck className="h-11 w-11 mx-auto text-emerald-600" />
            <CardTitle className="mt-3">Sign In & Accept Invitation</CardTitle>
            <p className="text-sm text-gray-500 mt-1">
              You've been invited to join <strong>{orgName}</strong> as <strong>{roleName}</strong>.
            </p>
            <p className="text-xs text-gray-400 mt-1">Sign in with your existing APC EstateFlow password.</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={acceptExisting} className="space-y-4" data-testid="accept-invite-form">
              <div><Label>Email</Label><Input type="email" required value={loginEmail} onChange={e => setLoginEmail(e.target.value)} placeholder="Your APC EstateFlow email" data-testid="accept-email" /></div>
              <div><Label>Password</Label><Input type="password" required value={loginPassword} onChange={e => setLoginPassword(e.target.value)} placeholder="Your existing password" data-testid="accept-password" /></div>
              <Button className="w-full bg-emerald-600 hover:bg-emerald-700" disabled={saving} data-testid="accept-signin-btn">
                {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Signing in...</> : 'Sign In & Accept Invitation'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // New user: activate with password
  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <ShieldCheck className="h-11 w-11 mx-auto text-emerald-600" />
          <CardTitle className="mt-3">Activate Account & Set Password</CardTitle>
          <p className="text-sm text-gray-500 mt-1">
            You've been invited to join <strong>{orgName}</strong> as <strong>{roleName}</strong>.
          </p>
          <p className="text-xs text-gray-400 mt-1">Create a password to activate your APC EstateFlow account.</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={activateNew} className="space-y-4" data-testid="staff-activate-form">
            <div><Label>New Password</Label><Input type="password" minLength={8} required value={password} onChange={e => setPassword(e.target.value)} data-testid="staff-password-input" /></div>
            <div><Label>Confirm Password</Label><Input type="password" minLength={8} required value={confirm} onChange={e => setConfirm(e.target.value)} data-testid="staff-confirm-input" /></div>
            <Button className="w-full" disabled={saving || !token} data-testid="staff-activate-btn">{saving ? 'Activating...' : 'Activate Account'}</Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
